// Email Delivery Log — staff-facing page to see whether DashONE invitation /
// password emails actually reached recipients (delivered vs. queued / failed /
// bounced). Helps triage "I never got the invite" reports without logging into
// Mailgun. Data: GET /email-events (dashone apps/api), a staff-only proxy to the
// Mailgun Logs API, filtered to DashONE onboarding/password subjects.
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Tag,
  Tooltip,
  Select,
  Space,
  Spin,
  Alert,
} from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { fetchEmailEvents } from "../api/emailApi";
import { foldSearch } from "../utils/searchFold";

const DURATION_OPTIONS = [
  { value: "1d", label: "Last 24 hours" },
  { value: "3d", label: "Last 3 days" },
  { value: "5d", label: "Last 5 days" },
];

// Mailgun event → Tag color. delivered = good; failed/rejected = bad;
// accepted = handed off, not yet delivered.
const eventTag = (event) => {
  switch (event) {
    case "delivered":
      return { color: "success", label: "Delivered" };
    case "failed":
      return { color: "error", label: "Failed" };
    case "rejected":
      return { color: "error", label: "Rejected" };
    case "accepted":
      return { color: "processing", label: "Accepted" };
    default:
      return { color: "default", label: event || "—" };
  }
};

const fmtTimestamp = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
};

const EmailDeliveryLogPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState("5d");
  const [search, setSearch] = useState("");

  const load = useCallback((dur) => {
    setLoading(true);
    setError(null);
    fetchEmailEvents({ duration: dur })
      .then((resp) => setData(resp))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => load(duration), [load, duration]);

  const rows = useMemo(() => {
    const items = data?.items || [];
    return items.map((it, i) => ({ key: `${it.id || i}`, ...it }));
  }, [data]);

  const filtered = useMemo(() => {
    const q = foldSearch(search);
    if (!q) return rows;
    return rows.filter((r) => foldSearch(r.recipient).includes(q));
  }, [rows, search]);

  const columns = [
    {
      title: "Time",
      dataIndex: "timestamp",
      width: 200,
      defaultSortOrder: "descend",
      sorter: (a, b) => (a.timestamp || "").localeCompare(b.timestamp || ""),
      render: (ts) => fmtTimestamp(ts),
    },
    {
      title: "Recipient",
      dataIndex: "recipient",
      width: 260,
      sorter: (a, b) => (a.recipient || "").localeCompare(b.recipient || ""),
    },
    {
      title: "Subject",
      dataIndex: "subject",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "event",
      width: 130,
      filters: [
        { text: "Delivered", value: "delivered" },
        { text: "Accepted", value: "accepted" },
        { text: "Failed", value: "failed" },
        { text: "Rejected", value: "rejected" },
      ],
      onFilter: (value, record) => record.event === value,
      render: (event) => {
        const t = eventTag(event);
        return <Tag color={t.color}>{t.label}</Tag>;
      },
    },
    {
      title: "Reason",
      dataIndex: "reason",
      ellipsis: true,
      render: (reason) =>
        reason ? <Tooltip title={reason}>{reason}</Tooltip> : "—",
    },
  ];

  if (loading) {
    return (
      <div
        className="ml-[240px]"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-[calc(100%-240px)] ml-[240px]" style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Email delivery log unavailable"
          description={error}
        />
      </div>
    );
  }

  return (
    <div className="w-[calc(100%-240px)] ml-[240px]" style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
            Email Delivery Log
          </h1>
          <span style={{ color: "#6b7280", fontSize: 13 }}>
            DashONE invitation &amp; password emails ({filtered.length} shown)
          </span>
        </div>
        <Space wrap>
          <Select
            value={duration}
            onChange={setDuration}
            options={DURATION_OPTIONS}
            style={{ width: 160 }}
          />
          <Input
            allowClear
            placeholder="Search recipient"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => load(duration)}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {data?.truncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Showing the most recent events only"
          description="This window has more events than were fetched; narrow the time range to see older entries."
        />
      )}

      <Table
        size="small"
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 980 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100],
          showTotal: (t) => `Total ${t}`,
        }}
      />
    </div>
  );
};

export default EmailDeliveryLogPage;
